import type { QuoteSection, QuoteSectionInput } from '@catalyst-psa/shared';
import {
  QUOTE_LEGAL_SECTION_INDEX,
  QUOTE_LEGAL_SECTION_TITLE,
  QUOTE_SECTION_COUNT,
  QUOTE_TEXT_VARIABLES,
} from '@catalyst-psa/shared';

import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { RichTextEditor, RichTextContent } from '../../components/ui/RichText.js';

export interface SectionForm {
  sectionIndex: number;
  title: string;
  html: string;
  pageBreakBefore: boolean;
  pageBreakAfter: boolean;
  /** Hidden sections keep their slot but are excluded from the PDF + delivery page. */
  hidden: boolean;
}

/** Builds the editable section forms from a quote/template's stored sections. */
export function toSectionForms(sections: QuoteSection[] | undefined): SectionForm[] {
  const byIndex = new Map((sections ?? []).map((s) => [s.sectionIndex, s]));
  const numbered = Array.from({ length: QUOTE_SECTION_COUNT }, (_, i) => {
    const index = i + 1;
    const s = byIndex.get(index);
    return {
      sectionIndex: index,
      // A stored row with a null title was deliberately cleared — keep it blank.
      title: s ? (s.title ?? '') : `Section ${index}`,
      html: s?.html ?? '',
      pageBreakBefore: s?.pageBreakBefore ?? false,
      pageBreakAfter: s?.pageBreakAfter ?? false,
      hidden: s?.hidden ?? false,
    };
  });
  // The dedicated Legal Contract slot always edits (and renders) at the bottom.
  const legal = byIndex.get(QUOTE_LEGAL_SECTION_INDEX);
  return [
    ...numbered,
    {
      sectionIndex: QUOTE_LEGAL_SECTION_INDEX,
      title: legal ? (legal.title ?? '') : QUOTE_LEGAL_SECTION_TITLE,
      html: legal?.html ?? '',
      pageBreakBefore: legal?.pageBreakBefore ?? false,
      pageBreakAfter: legal?.pageBreakAfter ?? false,
      hidden: false,
    },
  ];
}

/** Converts the section forms back into the API input shape. */
export function toSectionInputs(forms: SectionForm[]): QuoteSectionInput[] {
  return forms.map((f) => ({
    sectionIndex: f.sectionIndex,
    title: f.title.trim() || null,
    html: f.html || null,
    pageBreakBefore: f.pageBreakBefore,
    pageBreakAfter: f.pageBreakAfter,
    hidden: f.hidden,
  }));
}

interface QuoteSectionsEditorProps {
  sections: SectionForm[];
  onChange: (next: SectionForm[]) => void;
  readOnly?: boolean;
  /** When provided, pasted/dropped images upload via this callback (SharePoint). */
  onUploadImage?: (file: File) => Promise<{ id: string; url: string }>;
  /**
   * Which slots to render: the numbered sections, only the Legal Contract card, or
   * everything (default). Pages render two instances so the Legal Contract sits at the
   * very bottom of the editor, below the line sections.
   */
  slot?: 'numbered' | 'legal';
}

/** Read-only page-break divider shown where the PDF starts a new page. */
function PageBreakMarker(): JSX.Element {
  return (
    <div className="my-2 flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-400">
      <span className="h-px flex-1 border-t border-dashed border-slate-300" />
      Page break
      <span className="h-px flex-1 border-t border-dashed border-slate-300" />
    </div>
  );
}

/**
 * The section editor shared by quotes and quote templates: per-section editable
 * title (blank = no heading), page-break-before/after toggles, and a rich-text body.
 */
export function QuoteSectionsEditor({
  sections,
  onChange,
  readOnly = false,
  onUploadImage,
  slot,
}: QuoteSectionsEditorProps): JSX.Element {
  const patch = (index: number, changes: Partial<SectionForm>): void => {
    onChange(sections.map((s) => (s.sectionIndex === index ? { ...s, ...changes } : s)));
  };

  const visible = sections.filter((s) =>
    slot === 'legal'
      ? s.sectionIndex === QUOTE_LEGAL_SECTION_INDEX
      : slot === 'numbered'
        ? s.sectionIndex !== QUOTE_LEGAL_SECTION_INDEX
        : true,
  );

  return (
    <div className="space-y-4">
      {!readOnly && slot !== 'legal' ? (
        <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Insert PSA data with the <span className="font-medium">Variable</span> menu in the
          editor toolbar, or type a token like{' '}
          <code className="rounded bg-slate-100 px-1">[Customer.Name]</code> — it turns into a
          green chip when registered, and is replaced with live data on the PDF and the delivery
          page. Available:{' '}
          {QUOTE_TEXT_VARIABLES.map((v, i) => (
            <span key={v.token}>
              {i > 0 ? ' · ' : ''}
              <code className="rounded bg-slate-100 px-1">[{v.token}]</code>
            </span>
          ))}
        </p>
      ) : null}
      {visible.map((section) => {
        const isLegal = section.sectionIndex === QUOTE_LEGAL_SECTION_INDEX;
        return (
        <div
          key={section.sectionIndex}
          id={`quote-section-${section.sectionIndex}`}
          className={`scroll-mt-6 rounded-xl border bg-white p-4 shadow-sm ${
            isLegal ? 'border-brand-300' : 'border-slate-200'
          }${section.hidden ? ' opacity-60' : ''}`}
        >
          {readOnly && section.pageBreakBefore ? <PageBreakMarker /> : null}
          {isLegal ? (
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-700">
              Legal Contract
            </p>
          ) : null}
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0 flex-1">
              {readOnly ? (
                <p className="text-sm font-semibold text-slate-800">
                  {section.title ||
                    (isLegal ? QUOTE_LEGAL_SECTION_TITLE : `Section ${section.sectionIndex}`)}
                  {section.hidden ? (
                    <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">
                      Hidden
                    </span>
                  ) : null}
                </p>
              ) : (
                <TextField
                  label={isLegal ? 'Legal Contract title' : `Section ${section.sectionIndex} title`}
                  value={section.title}
                  maxLength={256}
                  placeholder="Leave blank for no title"
                  onChange={(e) => patch(section.sectionIndex, { title: e.target.value })}
                />
              )}
            </div>
            {!readOnly ? (
              <div className="flex flex-wrap items-center gap-4">
                {!isLegal ? (
                  <CheckboxField
                    label="Hide section"
                    checked={section.hidden}
                    onChange={(e) => patch(section.sectionIndex, { hidden: e.target.checked })}
                  />
                ) : null}
                <CheckboxField
                  label="Page break before this section"
                  checked={section.pageBreakBefore}
                  onChange={(e) =>
                    patch(section.sectionIndex, { pageBreakBefore: e.target.checked })
                  }
                />
              </div>
            ) : null}
          </div>

          {!readOnly && isLegal ? (
            <p className="mb-3 rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-700">
              Always renders at the bottom of the quote and cannot be hidden. A quote cannot be
              sent to the customer while this section is empty.
            </p>
          ) : null}

          {!readOnly && section.hidden ? (
            <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Hidden — this section will not appear on the PDF or the customer delivery page.
            </p>
          ) : null}

          {readOnly ? (
            section.html ? (
              <RichTextContent html={section.html} />
            ) : (
              <p className="text-sm text-slate-400">Empty section.</p>
            )
          ) : (
            <RichTextEditor
              value={section.html}
              onChange={(html) => patch(section.sectionIndex, { html })}
              placeholder={isLegal ? 'Legal contract content (MSA, license agreement)…' : 'Section content…'}
              onUploadImage={onUploadImage}
              withVariables
            />
          )}

          {readOnly && section.pageBreakAfter ? <PageBreakMarker /> : null}
          {!readOnly ? (
            <div className="mt-3">
              <CheckboxField
                label="Page break after this section"
                checked={section.pageBreakAfter}
                onChange={(e) => patch(section.sectionIndex, { pageBreakAfter: e.target.checked })}
              />
            </div>
          ) : null}
        </div>
        );
      })}
    </div>
  );
}
